import { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Switch } from '@blueprintjs/core';

import WidgetGrid from '../../pages/single-report/widget-grid';
import * as Selectors from '../../../selectors';
import { getOptions } from '../../../models/user';
import * as PeriodModel from '../../../models/period';
import * as PERIOD_PRESETS from '../../../constants/period-presets';
import { updateReportConfigByHierarchy } from '../../../utils/reports';
import { ORG_HAS_STAFF_DETECTION } from '../../../constants/features';
import LocationFilterButtons from '../../location-filter-buttons';
import {
  DemographicsBar,
  DemographicsPanelHeader,
  SwitchContainer,
} from '../../pages/summary/layout';

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%; /* fill space between TopBar and divider */
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;

  /* subtle dashed border */
  border: 1px dashed var(--mid-grey);
  border-radius: var(--widget-border-radius);

  /* Match TopBar Title styles */
  font-size: 18px;
  font-weight: 400;
  font-family: var(--fonts-heading);
  color: var(--widget-heading-text);
`;

function ReportPreviewPanelImpl({
  reportJson,
  allLocationsList,
  availableMetrics,
  startOfWeek,
  showStaffFilters,
  onDerivedConfigChange,
}) {
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!reportJson) {
      setReport(null);
      if (onDerivedConfigChange) onDerivedConfigChange(null);
      return;
    }
    const base = typeof reportJson === 'object' ? reportJson : {};
    const config = base.config || base;
    const next = {
      id: base.id || 'ai-preview',
      name: base.name || 'AI report preview',
      config: config || {},
    };
    setReport(next);
    if (onDerivedConfigChange) onDerivedConfigChange(next.config || {});
  }, [reportJson]);

  const updateReportConfig = ({
    property,
    newValue,
    hierarchyLevel,
    hierarchyIndex,
  }) => {
    if (!report) return;
    const next = updateReportConfigByHierarchy(
      report,
      property,
      newValue,
      hierarchyLevel,
      hierarchyIndex,
    );
    setReport(next);
    if (onDerivedConfigChange) onDerivedConfigChange(next.config || {});
  };

  const getHierarchyConfig = (parent = {}, child = {}) => ({
    locations: child.locations || parent.locations,
    period: child.period || parent.period,
    comparisonPeriod: child.comparisonPeriod || parent.comparisonPeriod,
    demographicFilter: child.demographicFilter || parent.demographicFilter,
  });

  const transformHierarchyConfig = (config = {}) => {
    let overrideDate;
    try {
      const params = new URL(window.document.location).searchParams;
      const overrideDateString = params.get('overrideDate');
      if (overrideDateString) overrideDate = new Date(overrideDateString);
    } catch (e) {
      // ignore
    }

    let period = PeriodModel.getExplorerPeriod(
      PERIOD_PRESETS.lastFullDay,
      overrideDate,
    );
    let comparisonPeriod = {};

    if (config.period) {
      if (
        config.period.selectedPreset === PERIOD_PRESETS.custom &&
        config.period.selectedDates
      ) {
        period = config.period;
      } else {
        period = PeriodModel.getExplorerPeriod(
          config.period.selectedPreset,
          overrideDate,
        );
      }
    }

    if (config.comparisonPeriod && period && period.selectedDates) {
      comparisonPeriod = PeriodModel.getExplorerComparePeriod(
        config.comparisonPeriod.selectedPreset,
        period.selectedDates,
      );
    }

    return {
      locations:
        config.locations === 'all'
          ? (allLocationsList || []).map((l) => l.id)
          : config.locations,
      period,
      comparisonPeriod,
      demographicFilter: config.demographicFilter,
    };
  };

  const topLevelConfig = useMemo(() => {
    const rc = (report && report.config) || {};
    const hc = getHierarchyConfig(rc);
    return transformHierarchyConfig(hc);
  }, [report, allLocationsList]);

  const handleToggleExcludeStaff = useCallback(() => {
    if (!report) return;
    const excludeStaff =
      Selectors.isDemographicFilterRoleCustomer(report.config) ||
      !showStaffFilters;
    const newValue = excludeStaff ? {} : { role: 'customer' };
    updateReportConfig({
      property: 'demographicFilter',
      newValue,
      hierarchyLevel: 0,
      hierarchyIndex: 0,
    });
  }, [report, showStaffFilters]);

  if (!report)
    return (
      <Placeholder>
        Your report will be displayed here once generated
      </Placeholder>
    );

  return (
    <div>
        <DemographicsBar>
          <DemographicsPanelHeader>
            {showStaffFilters && (
              <div>
                <SwitchContainer>
                  <Switch
                    checked={Selectors.isDemographicFilterRoleCustomer(
                      topLevelConfig,
                    )}
                    label="Exclude staff from counts"
                    onChange={handleToggleExcludeStaff}
                  />
                </SwitchContainer>
              </div>
            )}
            <div>
              <LocationFilterButtons
                locations={allLocationsList || []}
                filteredLocations={
                  (topLevelConfig && topLevelConfig.locations) || []
                }
                minimalistic
                updateFilteredLocations={(locations) =>
                  updateReportConfig({
                    property: 'locations',
                    newValue: locations,
                    hierarchyLevel: 0,
                    hierarchyIndex: 0,
                  })
                }
              />
            </div>
          </DemographicsPanelHeader>
        </DemographicsBar>

        <WidgetGrid
          report={report}
          getHierarchyConfig={getHierarchyConfig}
          transformHierarchyConfig={transformHierarchyConfig}
          updateReportConfig={updateReportConfig}
          availableMetrics={availableMetrics}
          allLocationsList={allLocationsList}
          startOfWeek={startOfWeek}
        />
      </div>
  );
}

function mapStateToProps(state) {
  return {
    allLocationsList: Selectors.getLocations(state),
    availableMetrics: Selectors.getAvailableMetrics(state),
    startOfWeek: getOptions(state.user && state.user.model).startOfWeek,
    showStaffFilters: Selectors.userHasFeature(ORG_HAS_STAFF_DETECTION, state),
  };
}

const ReportPreviewPanel = connect(mapStateToProps)(ReportPreviewPanelImpl);
export default ReportPreviewPanel;
